import { Injectable, inject } from '@angular/core';
import { AttributeBase, Leg } from '@approot/models';
import {
  AdminService,
  EntityMapperService,
  ProductsConfigService,
  TradesCaptureService
} from '@approot/services';
import { DateUtilities, Utilities, clone } from '@approot/utilities';
import { getAllOptions } from '../utilities/leg-attribute-utilities';
import {
  clearTextColor,
  disableAndWipeAttribute,
  makeTextCustomColor
} from '../utilities/leg.utilities';
import { LegDateService } from './leg-date.service';
import { LegElemService } from './leg-elem.service';
import { LegHelperService } from './leg-helper.service';
import { LegTradeFormListenersService } from './leg-trade-form-listeners.service';

@Injectable({
  providedIn: 'root'
})
export class LegTradingModeService {
  private entityMapperService = inject(EntityMapperService);
  constructor(
    private legHelperService: LegHelperService,
    private adminService: AdminService,
    private productsConfigService: ProductsConfigService,
    private legDateService: LegDateService,
    private legElemService: LegElemService,
    private legTradeFormListenersService: LegTradeFormListenersService,
    private tradesCaptureService: TradesCaptureService
  ) {}

  public resetTradeAttribute(
    attribute: AttributeBase<any>,
    todaysDate: string
  ): void {
    const form = this.legHelperService.form;
    // resets the trade attribute to its default value
    if (this.adminService.useDefaults) {
      // if we are using the defaults of new attributes
      // we need to wipe the values
      const controlType = attribute.controlType;
      if (controlType === 'dropdown' || controlType === 'multiSelect') {
        const foundAttribute =
          this.productsConfigService.productInfo.attributesDisplayList.find(
            attributeDisplay => attributeDisplay.shortName === attribute.key
          );
        const attributeDefaults = this.adminService.attributeDefaults;
        if (foundAttribute && attributeDefaults && attributeDefaults.length) {
          const defaultStored = attributeDefaults.find(
            defaultValue => defaultValue.key === foundAttribute.longName
          );

          if (defaultStored) {
            form.get(attribute.key).setValue(defaultStored.value);
            return;
          }
        }
        switch (attribute.key) {
          case 'counterPartyA':
          case 'counterPartyB':
            form.get(attribute.key).setValue(null);
            break;
          default:
            form
              .get(attribute.key)
              .setValue(getAllOptions(attribute.options)[0]);
            break;
        }
      } else {
        switch (attribute.key) {
          case 'mtcDate':
          case 'valuationAdjustmentAmortizationEndDate':
            break;
          case 'valuationAdjustmentAmortizationStartDate':
          case 'tradeDate':
            form.get(attribute.key).setValue(todaysDate);
            this.legHelperService.setDateOnDelay(attribute.key, todaysDate);
            break;
          case 'premiumPaymentDate':
            this.legDateService.setPremiumDefaultDate();
            break;
          default:
            form.get(attribute.key).setValue('', { emitEvent: false });
            break;
        }
      }
    } else {
      const controlType = attribute.controlType;
      const isDropdown = controlType === 'dropdown';
      const isSelectType = isDropdown || controlType === 'multiSelect';
      if (isSelectType) {
        form.get(attribute.key).setValue(isDropdown ? null : []);
      } else {
        form.get(attribute.key).setValue('');
      }
    }
  }

  public changeInTradeView(
    tradingMode: boolean,
    legIndex: number,
    firstLegTradeId: number,
    leg: Leg,
    allAttributesBase: AttributeBase<any>[]
  ): void {
    const form = this.legHelperService.form;

    if (!tradingMode && leg.productType === 'newdcd') {
      clearTextColor(leg, leg.missingAttributeOnPricing);
      leg.missingAttributeOnPricing = 'margin';
      leg.lastEditedDcdAttribute = 'depositRate';
      makeTextCustomColor(
        leg.missingAttributeOnPricing,
        Utilities.blueTextColor,
        leg
      );
    }

    if (tradingMode) {
      const nowDate = new Date();
      const timestamp = nowDate.getTime().toString();
      const todayDate = DateUtilities.dateToPrettyDate(
        DateUtilities.getDateInISOFormat(nowDate)
      );

      allAttributesBase?.forEach(attribute => {
        // we need to go through the trade attributes and reset them to their default values
        // also enabled the attribute form control
        const attributeCtrl = form.get(attribute.key);
        if (attribute.attributeType !== 'tradeAttribute' || !attributeCtrl) {
          return;
        }

        this.legElemService.handleNumberField(attribute);
        attributeCtrl.enable({ emitEvent: false });

        if (!this.legHelperService.presetsLoaded) {
          return;
        }

        switch (attribute.key) {
          case 'premiumPayer': // Premium Payer default value depends on value of Position
            const isPositionBuy = form.get('position').value === 'Buy';
            attributeCtrl.setValue(
              isPositionBuy ? 'Counterparty A' : 'Counterparty B'
            );
            break;
          case 'tradeId':
            attributeCtrl.setValue(
              legIndex === 0 ? firstLegTradeId : timestamp + (legIndex + 1),
              { emitEvent: false }
            );
            break;
          case 'timestamp':
            attributeCtrl.setValue(timestamp, { emitEvent: false });
            break;
          case 'linkId':
            attributeCtrl.setValue(firstLegTradeId);
            break;
          default:
            this.resetTradeAttribute(attribute, todayDate);
            break;
        }
      });
      if (form.get('forceCashSettlement')?.value === 'False') {
        disableAndWipeAttribute('forcedCashSettlementCurrency', form);
        disableAndWipeAttribute('forcedCashSettlementFixingSource', form);
      }
      this.legElemService.setToTradesElementsAfter();
    } else if (this.legHelperService.presetsLoaded) {
      // disable all trade controls
      leg.attributesAsFormControls.forEach(attribute => {
        if (attribute.attributeType === 'tradeAttribute') {
          if (form.get(attribute.key)) {
            form.get(attribute.key).disable({ emitEvent: false });
            attribute.info = '';
          }
        }
      });
    }
    this.legHelperService.onChangeInLegFn();
  }

  public handleTradeAttributes(
    tradingMode: boolean,
    firstLegTradeId: number,
    allAttributesBase: AttributeBase<any>[]
  ) {
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    if (rfqExecutionSettings.rfqLeg) {
      return;
    }

    this.legTradeFormListenersService.tradeFormListeners(leg);

    const todayDate = DateUtilities.dateToPrettyDate(
      DateUtilities.getDateInISOFormat()
    );

    if (
      form.get('tradeDate') &&
      this.adminService.useDefaults &&
      ((leg.preSetPosition && !leg.preSetPosition['tradeDate']) ||
        !leg.preSetPosition)
    ) {
      // set the trade date value to today if there is no tradeDate going to be set
      form.get('tradeDate').setValue(todayDate);
      this.legHelperService.setDateOnDelay(
        'tradeDate',
        form.get('tradeDate').value
      );
    }

    if (!this.legHelperService.leg.preSetPosition?.timestamp) {
      form.get('timestamp')?.setValue(new Date().getTime().toString(), {
        emitEvent: false
      });
    }

    if (form.get('tradeId')) {
      form.get('tradeId').setValue(new Date().getTime().toString(), {
        emitEvent: false
      });
    }

    this.changeInTradeView(
      tradingMode,
      this.legHelperService.legIndex,
      firstLegTradeId,
      leg,
      allAttributesBase
    );
  }

  public updateTradeAttributeElements(
    allAttributesBase: AttributeBase<any>[]
  ): void {
    allAttributesBase.forEach(attribute => {
      if (attribute.attributeType === 'tradeAttribute') {
        const ctrl = this.legHelperService.form.get(attribute.key);
        ctrl.markAsTouched();
        ctrl.updateValueAndValidity({ emitEvent: false });
        if (
          attribute.controlType === 'date' &&
          DateUtilities.isPrettyDate(ctrl.value)
        ) {
          ctrl.setErrors(null);
        }
      }
    });
  }

  public tradeCaptureSave(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const formValue = form.value;
    const identifier = formValue.tradeId.toString();
    const product = this.entityMapperService.cleanConfidentialFields(
      clone(this.formattedProduct),
      'saveTrade'
    );
    if (leg.productType === 'newdcd' && formValue.margin !== undefined) {
      formValue.margin = '';
    }

    this.tradesCaptureService
      .getUserInfoRequest({
        task: 'saveTrade',
        item: product
      })
      .subscribe(_ => {
        this.tradesCaptureService.addRemoveLocalTrade(
          'putItem',
          identifier,
          product
        );
        makeTextCustomColor('tradeId', Utilities.greenTextColor, leg);
      });
  }

  private get formattedProduct(): Record<string, any> {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    return this.productsConfigService.productFormatter(
      form.value,
      leg.attributes
    );
  }
}
